export class DateUtils {
  private static readonly DATE_FIELDS = [
    "dateOfBirth",
    "passportExpiryDate",
    "visaExpiryDate",
    "registeredDateOfMarriage",
    "dateOfCompletion",
    "elicosStartDate",
    "elicosEndDate",
    "startDate",
    "endDate",
    "oshcExpiryDate",
    "disabilityStartDate",
    "dateCommenced",
    "dateEnded",
  ];

  private static readonly MONTH_MAP: { [key: string]: string } = {
    JAN: "1",
    FEB: "2",
    MAR: "3",
    APR: "4",
    MAY: "5",
    JUN: "6",
    JUL: "7",
    AUG: "8",
    SEP: "9",
    OCT: "10",
    NOV: "11",
    DEC: "12",
  };

  private static readonly MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  static parseDate(input: string | Date | null | undefined): Date | null {
    if (!input) return null;

    if (input instanceof Date) {
      return isNaN(input.getTime()) ? null : input;
    }

    let dateStr = String(input).trim();

    dateStr = dateStr.replace(/\b(\d{1,2})(st|nd|rd|th)\b/i, "$1");

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("/");
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (
        dayNum >= 1 &&
        dayNum <= 31 &&
        monthNum >= 1 &&
        monthNum <= 12 &&
        yearNum >= 1900 &&
        yearNum <= 2100
      ) {
        const date = new Date(Date.UTC(yearNum, monthNum - 1, dayNum));
        if (
          date.getUTCFullYear() === yearNum &&
          date.getUTCMonth() === monthNum - 1 &&
          date.getUTCDate() === dayNum
        ) {
          return date;
        }
      }
      return null;
    }

    if (/^\d{1,2}\/[A-Z]{3}\/\d{4}$/i.test(dateStr)) {
      try {
        const [day, month, year] = dateStr.split("/");
        const monthNumber = this.MONTH_MAP[month.toUpperCase()];
        if (monthNumber) {
          const date = new Date(Date.UTC(parseInt(year), parseInt(monthNumber) - 1, parseInt(day)));
          return isNaN(date.getTime()) ? null : date;
        }
      } catch {
        return null;
      }
    }

    if (/^\d{4}$/.test(dateStr)) {
      return new Date(Date.UTC(parseInt(dateStr), 0, 1));
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }

  static toISO(input: string | Date | null | undefined): string | null {
    const date = this.parseDate(input);
    return date ? date.toISOString().split("T")[0] : null;
  }

  static toDisplay(input: string | Date | null | undefined): string {
    const date = this.parseDate(input);
    if (!date) return "";

    const day = date.getUTCDate().toString().padStart(2, "0");
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  static toMonthYear(input: string | Date | null | undefined): string {
    if (!input) return "Not Provided";

    const date = this.parseDate(input);
    if (!date) return "Invalid Date";

    const month = this.MONTH_NAMES[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${month}-${year}`;
  }

  static toLocalizedString(input: string | Date | null | undefined): string {
    const date = this.parseDate(input);
    if (!date) return "";

    return date.toLocaleDateString("en-AU", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  static isDateField(fieldName: string): boolean {
    return this.DATE_FIELDS.includes(fieldName);
  }

  static convertDatesInObject(obj: any): any {
    if (!obj || typeof obj !== "object") return obj;

    const result = { ...obj };

    Object.keys(result).forEach((key) => {
      if (typeof result[key] === "object" && result[key] !== null) {
        result[key] = this.convertDatesInObject(result[key]);
      } else if (this.isDateField(key) && typeof result[key] === "string") {
        const isoDate = this.toISO(result[key]);
        if (isoDate) {
          result[key] = isoDate;
        }
      }
    });

    return result;
  }

  static formatValueForDisplay(field: string, value: any): string {
    if (field.toLowerCase().includes("date") && value) {
      return this.toDisplay(value);
    }
    return String(value);
  }
}
